import { expectedChecks } from '../data/world/sources'
import { eventEquivalences } from '../data/world/eventEquivalences'
import { invasionMapNames } from '../data/guide/maps'
import { getEntry } from './guideIndex'
import { hasCoords, isPoint } from './worldFormat'
import { perAppearance } from './worldQueries'
import type { WorldData } from '../types/world'

/**
 * match       = el valor real coincide con la referencia
 * mismatch    = el valor real NO coincide
 * info        = valor real sin referencia con la que comparar
 * unavailable = no se pudo comprobar (archivo no cargado)
 */
export type CheckStatus = 'match' | 'mismatch' | 'info' | 'unavailable'

export type CountRow = { label: string; actual?: string; expected?: string; status: CheckStatus; note?: string }
export type RelationRow = { label: string; total: number; unresolved: number; samples: string[]; status: CheckStatus; note?: string }
export type InvasionRow = {
  eventId: string
  title: string
  linkStatus: string
  linkEvidence: string
  comparable: boolean
  perAppearance?: { actual: number; approved?: number; status: CheckStatus; inconsistent: boolean }
  maps?: { actual: string[]; approved: string[]; status: CheckStatus; missing: string[]; extra: string[] }
}

const fmt = (n: number) => n.toLocaleString('es-ES')
const sample = (ids: string[]) => ids.slice(0, 6)
const same = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x))

export function buildVerification(w: WorldData) {
  const rep = Object.fromEntries(w.reports.map((r) => [r.key, r]))
  const loaded = (k: string) => !!rep[k]?.loaded

  const count = (label: string, files: string[], actual: number | undefined, expected?: number, note?: string): CountRow => {
    if (!files.every(loaded) || actual === undefined) return { label, expected: expected !== undefined ? fmt(expected) : undefined, status: 'unavailable', note: 'Archivo no cargado: no se pudo comprobar.' }
    return { label, actual: fmt(actual), expected: expected !== undefined ? fmt(expected) : undefined, status: expected === undefined ? 'info' : actual === expected ? 'match' : 'mismatch', note }
  }

  const spots = w.spawns.filter((s) => s.kind === 'spot').length
  const inv = w.spawns.filter((s) => s.kind === 'invasion').length
  const noLoc = Array.from(w.monsters.keys()).filter((id) => !w.idx.locationsByMonster.has(id)).length
  const dungeon = Array.from(w.maps.values()).find((m) => m.name === 'Dungeon')
  const dLevels = (dungeon?.moveEntries.map((e) => e.minLevel).filter((v): v is number => v !== undefined) ?? [])
  const redDragon = Array.from(w.monsters.values()).find((m) => m.name === 'Red Dragon')
  const rdMaps = redDragon ? Array.from(new Set((w.idx.spawnsByMonster.get(redDragon.id) ?? []).map((s) => w.maps.get(s.mapId)?.name ?? `Mapa ${s.mapId}`))) : []
  const mapNamesList = Array.from(w.maps.values()).map((m) => m.name)
  const dupMapNames = Array.from(new Set(mapNamesList.filter((n, i) => mapNamesList.indexOf(n) !== i)))

  const counts: CountRow[] = [
    count('Filas leídas en 08_spawns.csv', ['spawns'], rep.spawns?.rows, worldSources('spawns', w)),
    count('Apariciones únicas cargadas', ['spawns'], w.spawns.length, 1029, 'Referencia histórica; el conteo oficial está en "Paquete 08/10/2026".'),
    count('Spots', ['spawns'], spots, expectedChecks.spots),
    count('Registros de invasión', ['spawns'], inv, expectedChecks.invasionSpawns),
    count('Monstruos (06_monsters.csv)', ['monsters'], w.monsters.size, 536),
    count('Monstruos sin ubicación', ['monsters', 'locations'], noLoc, expectedChecks.monstersWithoutLocation, 'Se muestran como "Ubicación no documentada".'),
    count('Relaciones de ubicación', ['locations'], w.locations.length, 524),
    count('Mapas únicos', ['maps'], w.maps.size, 77),
    count('Bolsas de recompensa', ['bags'], w.bags.size),
    count('Contenidos de bolsa (21_bag_rewards)', ['bagRewards'], w.bagRewards.length),
    count('Activadores de bolsa (17_bag_triggers)', ['bagTriggers'], w.bagTriggers.length),
    count('Eventos (27_events)', ['events'], w.events.size),
    count('Horarios de evento (28_event_schedules)', ['eventSchedules'], w.eventSchedules.length),
    count('Ítems', ['items'], w.items.size),
    count('Variantes de ítem', ['variants'], w.variants.size),
    count('Rutas de obtención', ['routes'], w.routes.length),
    count('Reglas de drop', ['rules'], w.rules.size),
    count('Objetivos de reglas', ['ruleTargets'], w.ruleTargets.length),
    count('Ítems directos de eventos', ['eventItems'], w.eventItems.length),
    {
      label: 'Accesos de Dungeon',
      actual: loaded('maps') ? (dungeon ? dLevels.join(', ') || 'sin niveles' : 'mapa no encontrado') : undefined,
      expected: expectedChecks.dungeonLevels.join(', '),
      status: !loaded('maps') ? 'unavailable' : same(dLevels.map(String), expectedChecks.dungeonLevels.map(String)) ? 'match' : 'mismatch',
    },
    {
      label: 'Mapas de Red Dragon (spawns.csv)',
      actual: loaded('spawns') ? rdMaps.join(', ') || 'sin registros' : undefined,
      expected: expectedChecks.redDragonMaps.join(', '),
      status: !loaded('spawns') ? 'unavailable' : same(rdMaps, expectedChecks.redDragonMaps) ? 'match' : 'mismatch',
    },
    {
      label: 'Coordenadas',
      actual: loaded('spawns') ? `${fmt(w.spawns.filter(isPoint).length)} puntuales · ${fmt(w.spawns.filter((s) => hasCoords(s) && !isPoint(s)).length)} áreas · ${fmt(w.spawns.filter((s) => !hasCoords(s)).length)} sin coordenadas` : undefined,
      status: loaded('spawns') ? 'info' : 'unavailable',
    },
  ]

  // ── Errores de carga ──
  const loadErrors = w.reports.filter((r) => !r.loaded || r.parseErrors.length > 0)

  // ── Duplicados ──
  const duplicates = w.reports.filter((r) => r.duplicateCount > 0 || r.emptyIds > 0)

  // ── Relaciones ──
  const rel = (label: string, total: number, bad: { id: string }[], files: string[], note?: string): RelationRow => ({
    label,
    total,
    unresolved: bad.length,
    samples: sample(bad.map((b) => b.id)),
    status: !files.every(loaded) ? 'unavailable' : bad.length ? 'mismatch' : 'match',
    note,
  })
  const relations: RelationRow[] = [
    rel('spawns → monstruo', w.spawns.length, w.spawns.filter((s) => !w.monsters.has(s.monsterId)), ['spawns', 'monsters']),
    rel('spawns → mapa', w.spawns.length, w.spawns.filter((s) => !w.maps.has(s.mapId)), ['spawns', 'maps']),
    rel('spawns.possible_maps → mapa', w.spawns.filter((s) => s.possibleMaps.length).length, w.spawns.filter((s) => s.possibleMaps.some((id) => !w.maps.has(id))), ['spawns', 'maps']),
    rel('monster_locations → monstruo', w.locations.length, w.locations.filter((l) => !w.monsters.has(l.monsterId)), ['locations', 'monsters']),
    rel('monster_locations → mapa', w.locations.length, w.locations.filter((l) => !w.maps.has(l.mapId)), ['locations', 'maps']),
    rel('Objetivos → regla / monstruo / mapa', w.ruleTargets.length, w.ruleTargets.filter((t) => !w.rules.has(t.ruleId) || (t.monsterId !== undefined && !w.monsters.has(t.monsterId)) || (t.mapId !== undefined && !w.maps.has(t.mapId))), ['ruleTargets', 'rules', 'monsters', 'maps']),
    rel('Reglas → ítem', w.rules.size, Array.from(w.rules.values()).filter((r) => r.itemId && !w.items.has(r.itemId)), ['rules', 'items']),
    rel('Rutas → ítem / variante / regla / evento', w.routes.length, w.routes.filter((r) => (r.itemId && !w.items.has(r.itemId)) || (r.variantId && !w.variants.has(r.variantId) && !w.items.has(r.variantId)) || (r.ruleId && !w.rules.has(r.ruleId)) || (r.eventDirectItemId && !w.idx.eventItemById.has(r.eventDirectItemId))), ['routes', 'items', 'variants', 'rules', 'eventItems']),
    rel('Rutas → ítems intermedios (via_item_ids)', w.routes.filter((r) => r.viaItemIds.length).length, w.routes.filter((r) => r.viaItemIds.some((id) => !w.variants.has(id) && !w.items.has(id))), ['routes', 'items', 'variants']),
    rel('Objetivos de ruta → ruta / monstruo / mapa', w.routeTargets.length, w.routeTargets.filter((t) => !w.idx.routeById.has(t.routeId) || (t.monsterId !== undefined && !w.monsters.has(t.monsterId)) || (t.mapId !== undefined && !w.maps.has(t.mapId))), ['routeTargets', 'routes', 'monsters', 'maps']),
    rel('Ítems de eventos → ítem / monstruo / mapa', w.eventItems.length, w.eventItems.filter((e) => (e.itemId && !w.items.has(e.itemId)) || (e.monsterId !== undefined && !w.monsters.has(e.monsterId)) || (e.mapId !== undefined && !w.maps.has(e.mapId))), ['eventItems', 'items', 'monsters', 'maps']),
  ]
  const unresolvedEvents = Array.from(w.eventLinks.values())
    .filter((l) => l.status !== 'verified')
    .sort((a, b) => a.eventId.localeCompare(b.eventId))

  // ── Invasiones aprobadas ──
  const invasions: InvasionRow[] = eventEquivalences
    .filter((e) => e.guideEntryId)
    .map((eq) => {
      const link = w.eventLinks.get(eq.eventId)
      const title = getEntry(eq.guideEntryId!)?.title ?? eq.guideEntryId!
      const comparable = link?.status === 'verified'
      const base: InvasionRow = {
        eventId: eq.eventId,
        title,
        linkStatus: link?.status ?? 'unmapped',
        linkEvidence: link?.evidence ?? 'Sin registros.',
        comparable,
      }
      if (!comparable) return base
      const pa = perAppearance(w, eq.eventId)
      const actualMaps = Array.from(new Set(w.spawns.filter((s) => s.eventId === eq.eventId).flatMap((s) => [s.mapId, ...s.possibleMaps]).map((id) => w.maps.get(id)?.name ?? `Mapa ${id}`))).sort()
      const approvedMaps = invasionMapNames(eq.guideEntryId!).slice().sort()
      const missing = approvedMaps.filter((n) => !actualMaps.includes(n))
      const extra = actualMaps.filter((n) => !approvedMaps.includes(n))
      return {
        ...base,
        perAppearance: {
          actual: pa.total,
          approved: eq.approvedPerAppearance,
          inconsistent: pa.inconsistent,
          status: eq.approvedPerAppearance === undefined ? 'info' : pa.total === eq.approvedPerAppearance && !pa.inconsistent ? 'match' : 'mismatch',
        },
        maps: { actual: actualMaps, approved: approvedMaps, missing, extra, status: missing.length || extra.length ? 'mismatch' : 'match' },
      }
    })

  const issues =
    counts.filter((c) => c.status === 'mismatch' || c.status === 'unavailable').length +
    loadErrors.length +
    relations.filter((r) => r.status !== 'match').length +
    invasions.filter((i) => !i.comparable || i.perAppearance?.status === 'mismatch' || i.maps?.status === 'mismatch').length

  return {
    counts,
    loadErrors,
    duplicates,
    dupMapNames,
    monstersCopy: w.monstersCopy,
    copyLoaded: false,
    relations,
    unresolvedEvents,
    invasions,
    issues,
    filesLoaded: w.reports.filter((r) => r.loaded).length,
    filesTotal: w.reports.length,
  }
}

function worldSources(key: string, w: WorldData) {
  return w.reports.find((r) => r.key === key)?.expected
}
